import { useRef, useCallback } from 'react';

interface LongPressOptions {
  onLongPress: () => void;
  onClick?: () => void;
  threshold?: number;
  preventDefault?: boolean;
}

export function useLongPress({
  onLongPress,
  onClick,
  threshold = 500,
  preventDefault = false // Changed default to false for better mobile scrolling
}: LongPressOptions) {
  const isLongPress = useRef(false);
  const isPressing = useRef(false);
  const timerId = useRef<NodeJS.Timeout | null>(null);
  const startPos = useRef<{ x: number; y: number } | null>(null);
  const startTime = useRef<number>(0);
  const hasMoved = useRef(false);
  const isScrolling = useRef(false);

  const start = useCallback((event: React.TouchEvent | React.MouseEvent) => {
    // Don't prevent default to allow natural scrolling
    // Only prevent if explicitly requested
    if (preventDefault) {
      event.preventDefault();
    }

    // Store starting position and time
    if ('touches' in event) {
      startPos.current = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
    } else {
      startPos.current = {
        x: event.clientX,
        y: event.clientY
      };
    }

    startTime.current = Date.now();
    isPressing.current = true;
    isLongPress.current = false;
    hasMoved.current = false;
    isScrolling.current = false;

    // Start the long press timer
    timerId.current = setTimeout(() => {
      // Only trigger long press if we haven't moved and aren't scrolling
      if (!hasMoved.current && !isScrolling.current && isPressing.current) {
        isLongPress.current = true;
        isPressing.current = false;
        onLongPress();
        
        // Add haptic feedback for mobile devices
        if ('vibrate' in navigator) {
          navigator.vibrate(50);
        }
      }
    }, threshold);

    // Add subtle visual feedback - only for non-touch devices to avoid interfering with scroll
    if (!('touches' in event)) {
      const target = event.currentTarget as HTMLElement;
      target.style.transform = 'scale(0.98)';
      target.style.transition = 'transform 0.1s ease';
    }
  }, [onLongPress, threshold, preventDefault]);

  const cancel = useCallback((event?: React.TouchEvent | React.MouseEvent, forceCancel = false) => {
    if (timerId.current) {
      clearTimeout(timerId.current);
      timerId.current = null;
    }

    // Reset visual feedback
    if (event && !('touches' in event)) {
      const target = event.currentTarget as HTMLElement;
      target.style.transform = '';
      target.style.transition = '';
    }

    // Only trigger onClick if:
    // 1. It wasn't a long press
    // 2. We were pressing
    // 3. We haven't moved significantly
    // 4. We weren't scrolling
    // 5. The press was quick enough (under 300ms for mobile taps)
    const pressDuration = Date.now() - startTime.current;
    const isQuickTap = pressDuration < 300;
    
    if (!isLongPress.current && 
        isPressing.current && 
        !hasMoved.current && 
        !isScrolling.current && 
        !forceCancel &&
        isQuickTap &&
        onClick) {
      // Small delay to ensure this isn't part of a scroll gesture
      setTimeout(() => {
        if (!isScrolling.current) {
          onClick();
        }
      }, 50);
    }

    isPressing.current = false;
    startPos.current = null;
    hasMoved.current = false;
    isScrolling.current = false;
  }, [onClick]);

  const move = useCallback((event: React.TouchEvent | React.MouseEvent) => {
    if (!isPressing.current || !startPos.current) return;

    // Get current position
    let currentX: number, currentY: number;
    if ('touches' in event) {
      currentX = event.touches[0].clientX;
      currentY = event.touches[0].clientY;
    } else {
      currentX = event.clientX;
      currentY = event.clientY;
    }

    // Calculate distance moved
    const deltaX = Math.abs(currentX - startPos.current.x);
    const deltaY = Math.abs(currentY - startPos.current.y);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // More generous movement threshold for mobile (25px instead of 10px)
    const movementThreshold = 'touches' in event ? 25 : 10;
    
    if (distance > movementThreshold) {
      hasMoved.current = true;
      
      // Detect if this is likely a scroll gesture
      // Vertical movement > horizontal movement suggests scrolling
      if (deltaY > deltaX && deltaY > 15) {
        isScrolling.current = true;
      }
      
      // Cancel the press
      cancel(event, true);
    }
  }, [cancel]);

  // Additional scroll detection
  const handleScroll = useCallback(() => {
    if (isPressing.current) {
      isScrolling.current = true;
      cancel(undefined, true);
    }
  }, [cancel]);

  // Listen for scroll events on the window to detect scrolling
  const handleWindowScroll = useCallback(() => {
    if (isPressing.current) {
      isScrolling.current = true;
      cancel(undefined, true);
    }
  }, [cancel]);

  return {
    onMouseDown: start,
    onMouseUp: cancel,
    onMouseLeave: cancel,
    onMouseMove: move,
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchCancel: cancel,
    onTouchMove: move,
    // Additional handlers for better scroll detection
    onScroll: handleScroll,
    onWheel: handleWindowScroll
  };
}